/**
 * Shared DXF handle normalization — single source of truth.
 *
 * Extracted from handleResolution.ts (Codex #251-A follow-up) so the OBJECTS
 * pre-scan paths (sectionParsers.parseObjectsSection, assocArrayParser
 * collectAssocArrays / computeAssocArrayCoreHandles) can normalize handle
 * comparisons WITHOUT importing handleResolution — handleResolution already
 * imports types from assocArrayParser, so a value import in the other
 * direction would create an assocArrayParser ↔ handleResolution cycle
 * (the repo's dependency-cruiser config enforces GLOBAL zero cycles).
 * handleResolution re-exports `normalizeHandle` for its existing consumers,
 * so no external import path changes.
 */

/**
 * Normalize a DXF handle for map keying / lookup.
 *
 * DXF handles are hexadecimal strings (group 5 / 330 / 340 / …). AutoCAD
 * and other producers are inconsistent about case — a STYLE table record
 * may carry `A7` (group 5) while an MLEADER references it as `a7`
 * (group 340). Hex `A7` and `a7` denote the SAME handle, so an
 * exact-string map lookup would spuriously miss and fall back to
 * "Standard" (Codex #243 F2). Normalize both the index keys AND every
 * lookup through this helper so case never breaks resolution. Trimmed
 * defensively in case a caller passes an un-trimmed value.
 *
 * Normalization is LOOKUP-ONLY: stored payloads (rawGroups, ownerHandle,
 * dependencyRefHandle, resolved sourceEntityIds, …) always keep the
 * ORIGINAL handle strings so round-trip fidelity is preserved.
 */
export function normalizeHandle(handle: string): string {
  return handle.trim().toLowerCase();
}
